import { TemplateResult, html } from 'lit';
import { isNullish } from '../../common/utils/nullish.js';
import {
  AnnualizedPeriodsPerformance,
  CurrentYearPeriodsPerformance,
  TotalReturnsArgs,
} from './total-returns.model.js';

export enum AnnualizedPerformancePeriod {
  TenYear = 'tenYear',
  FiveYear = 'fiveYear',
  ThreeYear = 'threeYear',
  OneYear = 'oneYear',
  SinceInception = 'sinceInception',
}

export enum CurrentYearPerformancePeriod {
  YearToDate = 'yearToDate',
  ThreeMonths = 'threeMonth',
  OneMonth = 'oneMonth',
}

export function hasAnnualizedPerformanceDataForPeriod(
  performanceData: AnnualizedPeriodsPerformance | null | undefined,
  annualizedPeriod: AnnualizedPerformancePeriod,
) {
  if (!performanceData) {
    return false;
  }
  return (
    !isNullish(performanceData.pureGross?.[annualizedPeriod]) &&
    !isNullish(performanceData.net?.[annualizedPeriod])
  );
}

export function hasAnnualizedPerformanceData(data: {
  monthEndPerformanceData?: AnnualizedPeriodsPerformance | null;
  quarterEndPerformanceData?: AnnualizedPeriodsPerformance | null;
}) {
  return (
    hasAnnualizedPerformanceDataForPeriod(
      data.monthEndPerformanceData,
      AnnualizedPerformancePeriod.SinceInception,
    ) ||
    hasAnnualizedPerformanceDataForPeriod(
      data.monthEndPerformanceData,
      AnnualizedPerformancePeriod.OneYear,
    ) ||
    hasAnnualizedPerformanceDataForPeriod(
      data.monthEndPerformanceData,
      AnnualizedPerformancePeriod.ThreeYear,
    ) ||
    hasAnnualizedPerformanceDataForPeriod(
      data.monthEndPerformanceData,
      AnnualizedPerformancePeriod.FiveYear,
    ) ||
    hasAnnualizedPerformanceDataForPeriod(
      data.monthEndPerformanceData,
      AnnualizedPerformancePeriod.TenYear,
    ) ||
    hasAnnualizedPerformanceDataForPeriod(
      data.quarterEndPerformanceData,
      AnnualizedPerformancePeriod.SinceInception,
    ) ||
    hasAnnualizedPerformanceDataForPeriod(
      data.quarterEndPerformanceData,
      AnnualizedPerformancePeriod.OneYear,
    ) ||
    hasAnnualizedPerformanceDataForPeriod(
      data.quarterEndPerformanceData,
      AnnualizedPerformancePeriod.ThreeYear,
    ) ||
    hasAnnualizedPerformanceDataForPeriod(
      data.quarterEndPerformanceData,
      AnnualizedPerformancePeriod.FiveYear,
    ) ||
    hasAnnualizedPerformanceDataForPeriod(
      data.quarterEndPerformanceData,
      AnnualizedPerformancePeriod.TenYear,
    )
  );
}

export function hasCurrentYearPerformanceDataForPeriod(
  performanceData: CurrentYearPeriodsPerformance | null | undefined,
  currentYearPeriod: CurrentYearPerformancePeriod,
) {
  if (!performanceData) {
    return false;
  }
  return (
    !isNullish(performanceData.pureGross?.[currentYearPeriod]) &&
    !isNullish(performanceData.net?.[currentYearPeriod])
  );
}

export function hasCurrentYearPerformanceData(
  performanceData: CurrentYearPeriodsPerformance | null | undefined,
) {
  if (!performanceData) {
    return false;
  }

  return (
    hasCurrentYearPerformanceDataForPeriod(
      performanceData,
      CurrentYearPerformancePeriod.OneMonth,
    ) ||
    hasCurrentYearPerformanceDataForPeriod(
      performanceData,
      CurrentYearPerformancePeriod.ThreeMonths,
    ) ||
    hasCurrentYearPerformanceDataForPeriod(
      performanceData,
      CurrentYearPerformancePeriod.YearToDate,
    )
  );
}

/**
 * Used to consistently generate component HTML with populated attributes
 */
export const generateHTML = (args: TotalReturnsArgs): TemplateResult => {
  return html`
    <pop-total-returns-component
      data-product-code="${args.productCode}"
      data-audience="${args.audience}"
      data-country="${args.country}"
      data-language="${args.language}"
      data-component-object="${JSON.stringify(args.componentObject)}"
    >
    </pop-total-returns-component>
  `;
};
